import { useLocation, useNavigate } from '@solidjs/router';
import { createEffect, createSignal, on, onCleanup, onMount, Show, useContext } from 'solid-js';
import { Portal } from 'solid-js/web';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import { ErrorCatcher } from '../../tools/error-catcher';
import { TabHandler } from '../../tools/tab-handler';
import { lockPageScroll } from '../../tools/scroll-lock';
import { closeSearchPanel, SEARCH_PANEL_TRIGGER_ATTR, searchPanelIsOpen } from '../../stores/search-store';
import { SearchAutocomplete } from './search-autocomplete';
import { buildSearchPageUrl } from './search-shared';

/* A hair longer than `--animate-cvi-drawer-top-out` (tailwind-theme-misc.css). Safety net for
   when `animationend` never reaches us — the band must not stay stuck open. */
const EXIT_FALLBACK_MS = 350;

/* The band that drops out under the site header when the header's search button is pressed
   (Figma "Search opened", 1436:10062 / 1436:10395). Not a side drawer: a full-width white
   strip below the navigation holding one search field, with the page dimmed underneath.
   Submitting goes to the search page; the results live there, not here.

   Rendered inside `<header>`, which is sticky and therefore the containing block, so the
   band hangs off the header's bottom edge without adding to its height. It slides down from
   under the navigation (the DS drawer motion, top-anchored) inside a clipping wrapper, so it
   never crosses the header rows. The scrim is portaled to the body so it sits between the
   header (z-3) and the page (z-1). */
export const SearchPanel = () => (
    <Show when={searchPanelIsOpen()}>
        <SearchPanelOpen />
    </Show>
);

const SearchPanelOpen = () => {
    const { localize, searchPage } = useContext(AppContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [query, setQuery] = createSignal('');
    const [inputEl, setInputEl] = createSignal<HTMLElement | undefined>();
    const [clearButtonEl, setClearButtonEl] = createSignal<HTMLElement | undefined>();

    /* Exit choreography, as in the country selector: the store unmounts us the moment it
       flips, so every close path goes through `requestClose`, which plays the slide-up first
       and only then tells the store. `closing` drives the out-classes. */
    const [closing, setClosing] = createSignal(false);
    /* The slide needs a clipping wrapper, but the suggestion menu hangs below the band and
       must not be clipped once the band is in place. Reduced motion has no enter animation
       and therefore no `animationend`, so it counts as entered from the start. */
    const [entered, setEntered] = createSignal(false);

    /* Tab cycles between the field and, once there is text, its clear button. */
    const lastTabbable = () => clearButtonEl() ?? inputEl();

    let panelRef: HTMLDivElement | undefined;
    let exitTimeout: ReturnType<typeof setTimeout> | undefined;
    let closed = false;
    let releaseScrollLock: (() => void) | undefined;

    const finishClose = () => {
        if (closed) return;
        closed = true;
        if (exitTimeout) clearTimeout(exitTimeout);
        closeSearchPanel();
        /* After the unmount, not before: TabHandler restores focus to what was focused when it
           mounted, which is the field itself (focused in onMount), and focusing a removed input
           lands on the body. */
        document.querySelector<HTMLElement>(`[${SEARCH_PANEL_TRIGGER_ATTR}]`)?.focus();
    };

    const requestClose = () => {
        if (closing()) return;

        /* `motion-reduce:animate-none` means no exit animation and no `animationend`. */
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            finishClose();
            return;
        }

        setClosing(true);
        exitTimeout = setTimeout(finishClose, EXIT_FALLBACK_MS);
    };

    const close = () => requestClose();

    const submit = (value: string) => {
        const trimmed = value.trim();

        if (!trimmed || !searchPage?.url) {
            return;
        }

        /* The route change below closes the band (effect further down); no slide-up first,
           the new page is the feedback. */
        finishClose();
        navigate(buildSearchPageUrl(searchPage.url, trimmed));
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            close();
        }
    };

    /* The panel is the only element whose exit animation we wait for; the scrim fades on
       the same clock but finishes independently. */
    const handlePanelAnimationEnd = (event: AnimationEvent) => {
        if (event.target !== panelRef) {
            return;
        }

        if (closing()) {
            finishClose();
            return;
        }

        setEntered(true);
    };

    onMount(() => {
        releaseScrollLock = lockPageScroll();
        window.addEventListener('keydown', handleKeyDown);
        inputEl()?.focus();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setEntered(true);
        }
    });

    onCleanup(() => {
        releaseScrollLock?.();
        window.removeEventListener('keydown', handleKeyDown);
        if (exitTimeout) clearTimeout(exitTimeout);
    });

    /* A header link clicked while the band is open navigates; the band must not follow. */
    createEffect(on(() => location.pathname, finishClose, { defer: true }));

    return (
        <ErrorCatcher componentName="Search panel">
            <TabHandler refs={{ firstTabbableElement: inputEl, lastTabbableElement: lastTabbable }}>
                <Portal mount={document.body}>
                    {/* Page dim from the design: 20% black, lighter than the drawer scrim. Click closes. */}
                    <div
                        aria-hidden="true"
                        class={clsx(
                            'fixed inset-0 z-[2] bg-cvi-bg-search-dim motion-reduce:animate-none',
                            closing() ? 'animate-cvi-backdrop-out' : 'animate-cvi-backdrop-in',
                        )}
                        onClick={close}
                    />
                </Portal>

                {/* Clip so the band appears to slide out from under the navigation; the border
                    sits on the wrapper so it is there from the first frame. */}
                <div
                    class={clsx(
                        'absolute inset-x-0 top-full z-[2] border-t border-cvi-border-muted',
                        (!entered() || closing()) && 'overflow-hidden',
                    )}
                >
                    <div
                        ref={panelRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label={localize('search', 'Search')}
                        onAnimationEnd={handlePanelAnimationEnd}
                        class={clsx(
                            'bg-cvi-bg-body motion-reduce:animate-none',
                            closing() ? 'animate-cvi-drawer-top-out' : 'animate-cvi-drawer-top-in',
                        )}
                    >
                        <Section
                            templateShorthand={[12]}
                            widthType="bgFull"
                            removeSpacingBlock={true}
                            customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
                        >
                            <div class="py-cvi-9">
                                <SearchAutocomplete
                                    id="header-search"
                                    class="max-w-cvi-search-field"
                                    value={query()}
                                    onInput={setQuery}
                                    onSubmit={submit}
                                    inputRef={setInputEl}
                                    clearButtonRef={setClearButtonEl}
                                    placeholder={localize('write-a-keyword', 'Write a keyword for what you are looking for…')}
                                />
                            </div>
                        </Section>
                    </div>
                </div>
            </TabHandler>
        </ErrorCatcher>
    );
};
